/**
 * Browser-kompatibler JWT-Decoder — KEINE Signatur-Verifikation.
 *
 * Verify ist EXPLIZIT Backend-Aufgabe. Der Frontend-Decoder ist nur Convenience,
 * um aus dem Access-Token die User-Identity, Role-Claims und Expiry zu lesen.
 *
 * Werden niemals Claims aus dem Frontend für Authorization-Entscheidungen genommen,
 * die der Server nicht NACHTRÄGLICH noch einmal prüft. UI-Visibility-Toggles sind
 * Convenience, nicht Security (siehe FRONTEND_GUIDELINES.md §8.5).
 */
import type { AuthUser, JwtClaims } from './types';

function base64UrlDecode(input: string): string {
  const padding = '='.repeat((4 - (input.length % 4)) % 4);
  const base64 = (input + padding).replace(/-/g, '+').replace(/_/g, '/');
  // atob ist DOM-API — in Node-Tests muss man eine Buffer-Polyfill stellen, aber wir
  // laufen im Browser, also reicht das.
  const raw = atob(base64);
  // UTF-8 decode für nicht-ASCII Claims (Email mit Umlauten etc.)
  return decodeURIComponent(
    raw
      .split('')
      .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
      .join(''),
  );
}

export function decodeJwt(token: string): JwtClaims | null {
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) {
    return null;
  }
  try {
    const payload = base64UrlDecode(parts[1]);
    return JSON.parse(payload) as JwtClaims;
  } catch {
    return null;
  }
}

export function jwtToAuthUser(token: string): AuthUser | null {
  const claims = decodeJwt(token);
  if (!claims) return null;
  const roles = Array.isArray(claims.role) ? claims.role : claims.role ? [claims.role] : [];
  const privileges = Array.isArray(claims.privilege)
    ? claims.privilege
    : claims.privilege
      ? [claims.privilege]
      : [];
  return {
    userId: claims.sub,
    tenantId: claims.tenant_id,
    email: claims.email,
    roles,
    jti: claims.jti,
    expiresAt: new Date(claims.exp * 1000),
    workspaceSlug: claims.workspace_slug,
    workspaceName: claims.workspace_name,
    workspaceKind: claims.workspace_kind,
    principalKind: claims.principal_kind,
    privilegeCatalogVersion: claims.privilege_catalog_version,
    privileges,
    ...(typeof claims.session_family_id === 'string'
      ? { sessionFamilyId: claims.session_family_id }
      : {}),
    ...(typeof claims.support_session_id === 'string'
      ? { supportSessionId: claims.support_session_id, homeTenantId: claims.home_tenant_id }
      : {}),
  };
}

export function isExpired(token: string, skewSeconds = 30): boolean {
  const claims = decodeJwt(token);
  if (!claims?.exp) return true;
  const now = Math.floor(Date.now() / 1000);
  return claims.exp <= now + skewSeconds;
}
